import React, { useEffect, useLayoutEffect, useState } from 'react';
import { toastr } from 'react-redux-toastr';
import PropTypes from 'prop-types';
import { Field, reduxForm, formValueSelector } from 'redux-form';
import { Form, Button, FormGroup } from 'reactstrap';
import { connect } from 'react-redux';
import FormItem from '../../../common/forms/FormItem';
import Select from '../../../common/forms/select/Select';
import {
  getCustomerEmails,
  clearCustomerEmails,
  getCustomerDeliveryAddresses,
  clearCustomerDeliveryAddresses,
  getPaymentMethods,
  getVoucherTypes,
  postCustomerEmail,
  postCustomerDeliveryAddress,
  clearCreditNotes,
  postPostpaidBookingPretransactionV2,
} from '../../../../actions/';
import { isRequired, validateEmail } from '../../../../utils/validators';
import CreatableSelect from '../../../common/forms/select/CreatableSelect';
import {
  TRANSACTION_TYPE_PAGO_EFECTIVO,
  TRANSACTION_TYPE_DELIVERY_CREDIT_CARD,
  TRANSACTION_TYPE_DELIVERY_CASH,
  EMAIL_PATTERN,
  TRANSACTION_TYPE_NIUBIZ,
  NIUBIZ_DOCUMENT_TYPE,
  CIP_DOCUMENT_TYPE,
  CREDIT_NOTE,
  TRANSACTION_TYPE_OPENPAY_LINK,
  DEFAULT_ORDER,
  TRANSACTION_TYPE_OTHER,
} from '../../../../config/constants';
import VoucherTypeInputGroup from '../../../common/forms/VoucherTypeInputGroup';
import CustomerInputGroup from '../../../common/forms/CustomerInputGroup';
import PagoEfectivoFields from './PagoEfectivoFields';
import NiubizFields from './NiubizFields';
import OpenpayFields from './OpenpayFields';
import TextInput from '../../../common/forms/input/TextInput';
import PaymentMethodSelect from '../../../common/forms/select/PaymentMethodSelect';
import CreditNoteSelect from '../../../common/forms/select/CreditNoteSelect';
import { passengersPropType } from './ReceiverPropTypes';
import { numberFormatter } from '../../../../utils/number';
import {
  clearDiscountCodeSubCategories,
  getDiscountCodeSubCategoriesByDiscountId,
} from '../../../../actions/sales/DiscountCodeSubCategory';
import FreeOfChargeCategorySelect from '../../../common/forms/select/FreeOfChargeCategorySelect';
import {
  clearFreeOfChargeCategory,
  getFreeOfChargeCategory,
} from '../../../../actions/sales/FreeOfChargeCategory';

const formatCreateLabel = (inputValue) => <span>Registrar {inputValue}</span>;

const isValidEmail = (optionValue) => {
  const email = optionValue.label;
  return validateEmail(email);
};

const isValidNiubizLink = (value) =>
  value || value === '0' || value === 0
    ? undefined
    : 'Generar el link es requerido';

const selector = formValueSelector('ReceiverForm');

const ReceiverForm = ({
  change,
  emails,
  totalPrice,
  passengers,
  submitting,
  creditNotes,
  handleSubmit,
  isEmailLoading,
  customerOrderId,
  selectedCustomer,
  deliveryAddresses,
  isPaymentMethodLoading,
  dispatchGetVoucherTypes,
  dispatchClearCreditNotes,
  isDeliveryAddressLoading,
  dispatchGetPaymentMethods,
  dispatchGetCustomerEmails,
  dispatchClearCustomerEmails,
  dispatchGetCustomerDeliveryAddresses,
  dispatchClearCustomerDeliveryAddresses,
  dispatchPostPostpaidBookingPretransactionV2,
  dispatchGetDiscountCodeSubCategoriesByDiscountId,
  dispatchClearDiscountCodeSubCategories,
  discountCodeId,
  discountCodeSubCategories,
  subCategoryAuthorizers,
  dispatchGetFreeOfChargeCategory,
  dispatchClearFreeOfChargeCategory,
  freeOfChargeSubCategories,
  freeOfChargeUsers,
}) => {
  useLayoutEffect(() => {
    dispatchGetPaymentMethods();
    dispatchGetVoucherTypes();

    if (discountCodeId) {
      dispatchGetDiscountCodeSubCategoriesByDiscountId(discountCodeId, {
        page: 0,
        size: 1000,
        sort: 'id,desc',
        order: DEFAULT_ORDER,
      });
    }

    return () => {
      dispatchClearCreditNotes();
      dispatchClearDiscountCodeSubCategories();
      dispatchClearFreeOfChargeCategory();
    };
  }, []);

  const [showVoucherCode, setShowVoucherCode] = useState(false);
  const [showDeliveryAddress, setShowDeliveryAddress] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [showCreditNoteSelect, setShowCreditNoteSelect] = useState(false);
  const [remainingPrice, setRemainingPrice] = useState(totalPrice);
  const [showDiscountSubCategory, setShowDiscountSubCategory] = useState(false);
  const [showFreeOfChargeFields, setShowFreeOfChargeFields] = useState(false);
  const [showFreeOfChargeSubCategory, setShowFreeOfChargeSubCategory] =
    useState(false);

  useEffect(() => {
    if (creditNotes.length > 0) {
      setRemainingPrice(
        totalPrice -
          creditNotes.reduce(
            (accumulated, creditNote) => accumulated + creditNote.amount,
            0,
          ),
      );
    }
  }, [creditNotes]);

  useEffect(() => {
    const hasOneSubcat =
      Array.isArray(discountCodeSubCategories) &&
      discountCodeSubCategories.length === 1;
    const hasOneAuthorizer =
      Array.isArray(subCategoryAuthorizers) &&
      subCategoryAuthorizers.length === 1;

    if (hasOneSubcat) {
      const onlySubcat = discountCodeSubCategories[0];
      change('discountCodeSubCategory', onlySubcat);

      if (hasOneAuthorizer) {
        const onlyAuth = subCategoryAuthorizers[0];
        change('discountCodeSubCategoryAuthorizer', onlyAuth);
        setShowDiscountSubCategory(false);
        return;
      }

      setShowDiscountSubCategory(true);
    } else {
      setShowDiscountSubCategory(
        Array.isArray(discountCodeSubCategories) &&
          discountCodeSubCategories.length >= 1,
      );
    }
  }, [discountCodeSubCategories, subCategoryAuthorizers, change]);

  useEffect(() => {
    const hasOneSubcat =
      Array.isArray(freeOfChargeSubCategories) &&
      freeOfChargeSubCategories.length === 1;

    const hasOneAuthorizer =
      Array.isArray(freeOfChargeUsers) && freeOfChargeUsers.length === 1;

    if (hasOneSubcat) {
      const onlySubcat = freeOfChargeSubCategories[0];
      change('freeOfChargeSubCategory', onlySubcat);

      if (hasOneAuthorizer) {
        const onlyAuth = freeOfChargeUsers[0];
        change('freeOfChargeUser', onlyAuth);
        setShowFreeOfChargeSubCategory(false);
        return;
      }

      setShowFreeOfChargeSubCategory(true);
    } else {
      setShowFreeOfChargeSubCategory(
        Array.isArray(freeOfChargeSubCategories) &&
          freeOfChargeSubCategories.length >= 1,
      );
    }
  }, [freeOfChargeSubCategories, freeOfChargeUsers, change]);

  const handleChangeEmail = (option) =>
    change('userEmail', option ? option.label : null);

  const handleCustomerChange = ({
    value: customerId,
    firstName,
    lastName,
    email,
    idDocumentNumber,
    mobilePhone,
  }) => {
    dispatchClearCustomerEmails();
    dispatchClearCustomerDeliveryAddresses();

    change('userName', null);
    change('userLastName', null);
    change('userEmail', null);
    change('userDocumentNumber', null);
    change('userPhone', null);

    if (customerId) {
      dispatchGetCustomerEmails({ customerId });
      dispatchGetCustomerDeliveryAddresses({ customerId });
      change('userName', firstName);
      change('userLastName', lastName);
      change('userEmail', email);
      change('userDocumentNumber', idDocumentNumber);
      change('userPhone', mobilePhone);
    }
  };

  const isValidNewEmail = (inputValue, selectValue, selectOptions) => {
    const existsInOptions = selectOptions.find(
      (option) => option.label === inputValue,
    );

    const isInputEmpty = inputValue.trim().length === 0;

    const validEmail = EMAIL_PATTERN.test(inputValue);

    return !isInputEmpty && !existsInOptions && validEmail && selectedCustomer;
  };

  const handleNoEmails = () => {
    if (!selectedCustomer)
      return <p className="text-danger">Debe seleccionar un destinatario</p>;

    return <p>Ingrese un email válido para ser registrado</p>;
  };

  const handleCreateEmail = async (inputValue) => {
    const newCustomerEmail = await postCustomerEmail({
      customerId: selectedCustomer.value,
      email: inputValue,
    });

    dispatchGetCustomerEmails({
      customerId: selectedCustomer.value,
    });

    change('email', {
      value: newCustomerEmail.id,
      label: newCustomerEmail.email,
    });
    change('userEmail', newCustomerEmail.email);
  };

  const handlePaymentMethodChange = (selectedPaymentMethod) => {
    setShowVoucherCode(
      selectedPaymentMethod.value &&
        selectedPaymentMethod.transactionCode ===
          TRANSACTION_TYPE_PAGO_EFECTIVO,
    );

    setPaymentMethod(selectedPaymentMethod.transactionCode);

    if (
      selectedPaymentMethod.transactionCode === TRANSACTION_TYPE_PAGO_EFECTIVO
    ) {
      change('userDocumentType', CIP_DOCUMENT_TYPE.DNI);
    }

    if (selectedPaymentMethod.transactionCode === TRANSACTION_TYPE_NIUBIZ) {
      change('userDocumentType', NIUBIZ_DOCUMENT_TYPE.DNI);
    }

    setShowFreeOfChargeFields(
      selectedPaymentMethod.transactionCode &&
        selectedPaymentMethod.transactionCode === TRANSACTION_TYPE_OTHER,
    );

    setShowDeliveryAddress(
      selectedPaymentMethod.value &&
        [
          TRANSACTION_TYPE_DELIVERY_CASH,
          TRANSACTION_TYPE_DELIVERY_CREDIT_CARD,
        ].includes(selectedPaymentMethod.transactionCode),
    );
    setShowCreditNoteSelect(
      selectedPaymentMethod.value &&
        selectedPaymentMethod.transactionCode === CREDIT_NOTE,
    );

    if (showVoucherCode) change('voucherCode', '');

    if (showDeliveryAddress) change('deliveryAddress', null);
  };

  const handleNextPaymentMethodChange = (selectedPaymentMethod) => {
    handlePaymentMethodChange(selectedPaymentMethod);
  };

  const isValidNewDeliveryAddress = (inputValue, selectOptions) => {
    const existsInOptions = selectOptions.find(
      (option) => option.label === inputValue,
    );

    const isInputEmpty = inputValue.trim().length === 0;

    return !isInputEmpty && !existsInOptions && selectedCustomer;
  };

  const handleNoDeliveryAddress = () => {
    if (!selectedCustomer)
      return <p className="text-danger">Debe seleccionar un destinatario</p>;

    return (
      <p>No hay direcciones registradas, ingrese una para ser registrada</p>
    );
  };

  const handleCreateDeliveryAddress = async (inputValue) => {
    const newCustomerDeliveryAddress = await postCustomerDeliveryAddress({
      customerId: selectedCustomer.value,
      deliveryAddress: inputValue,
    });

    dispatchGetCustomerDeliveryAddresses({
      customerId: selectedCustomer.value,
    });

    change('deliveryAddress', {
      value: newCustomerDeliveryAddress.id,
      label: newCustomerDeliveryAddress.deliveryAddress,
    });
  };

  const handleReceiverSubmit = (formValues) => {
    if (showCreditNoteSelect && creditNotes.length === 0) {
      toastr.error('Error', 'No se ha aplicado ninguna nota de crédito');
      return;
    }
    const postpaidPretransactionList = [];
    let currentRemainingPrice = totalPrice;
    creditNotes
      .sort((a, b) => a.amount - b.amount)
      .forEach((creditNote) => {
        let creditNoteAmount = 0;
        if (currentRemainingPrice === 0) return;
        if (currentRemainingPrice > creditNote.amount) {
          currentRemainingPrice -= creditNote.amount;
          creditNoteAmount = creditNote.amount;
        } else {
          creditNoteAmount = currentRemainingPrice;
          currentRemainingPrice = 0;
        }
        const postpaidPreTransaction = {
          customerOrderId,
          customerEmailAddressId: formValues.email.value,
          customerDeliveryAddressId: formValues.deliveryAddress
            ? formValues.deliveryAddress.value
            : null,
          paymentMethodId: formValues.paymentMethod.value,
          voucherTypeId: formValues.voucherType.value,
          businessId: formValues.business ? formValues.business.value : null,
          voucherCode: formValues.voucherCode || null,
          creditNoteId: creditNote.value,
          amount: creditNoteAmount,
          businessTaxId: formValues.businessTaxId,
          businessName: formValues.businessName,
          addressSummary: formValues.addressSummary,
        };
        postpaidPretransactionList.push(postpaidPreTransaction);
      });

    if (currentRemainingPrice !== 0 || !showCreditNoteSelect) {
      const postpaidPreTransaction = {
        customerOrderId,
        customerEmailAddressId: formValues.email.value,
        customerDeliveryAddressId: formValues.deliveryAddress
          ? formValues.deliveryAddress.value
          : null,
        paymentMethodId: formValues.nextPaymentMethod
          ? formValues.nextPaymentMethod.value
          : formValues.paymentMethod.value,
        voucherTypeId: formValues.voucherType.value,
        businessId: formValues.business ? formValues.business.value : null,
        voucherCode: formValues.voucherCode || null,
        amount: currentRemainingPrice,
        businessTaxId: formValues.businessTaxId,
        businessName: formValues.businessName,
        addressSummary: formValues.addressSummary,
      };
      postpaidPretransactionList.push(postpaidPreTransaction);
    }

    let discountCodeSubCategoryId = null;
    if (
      Array.isArray(discountCodeSubCategories) &&
      discountCodeSubCategories.length === 1
    ) {
      discountCodeSubCategoryId = discountCodeSubCategories[0].value;
    } else if (formValues.discountCodeSubCategory) {
      discountCodeSubCategoryId = formValues.discountCodeSubCategory.value;
    }

    let discountCodeAuthorizerId = null;

    if (formValues.discountCodeSubCategoryAuthorizer) {
      discountCodeAuthorizerId =
        formValues.discountCodeSubCategoryAuthorizer.value;
    } else if (
      Array.isArray(subCategoryAuthorizers) &&
      subCategoryAuthorizers.length === 1
    ) {
      discountCodeAuthorizerId = subCategoryAuthorizers[0].value;
    }

    const freeOfChargeCategoryId = formValues.freeOfChargeCategoryId
      ? formValues.freeOfChargeCategoryId.value
      : null;

    const freeOfChargeSubCategoryId = formValues.freeOfChargeSubCategory
      ? formValues.freeOfChargeSubCategory.value
      : null;

    const freeOfChargeUserId = formValues.freeOfChargeUser
      ? formValues.freeOfChargeUser.value
      : null;

    const newFormValues = {
      discountCodeSubCategoryId,
      discountCodeAuthorizerId,
      freeOfChargeCategoryId,
      freeOfChargeSubCategoryId,
      freeOfChargeUserId,
      postpaidPretransactionList,
    };

    dispatchPostPostpaidBookingPretransactionV2(newFormValues);
  };

  const voucherCodeField = showVoucherCode && (
    <>
      <hr />
      <FormGroup row>
        <FormItem label="Código">
          <Field
            name="voucherCode"
            component={TextInput}
            validate={[isRequired]}
            disabled
          />
        </FormItem>
      </FormGroup>
    </>
  );

  const voucherCode = (
    <>
      <PagoEfectivoFields
        selector={selector}
        show={showVoucherCode}
        form="ReceiverForm"
        remainingPrice={remainingPrice}
      />
      {voucherCodeField}
    </>
  );

  const niubizForm = paymentMethod === TRANSACTION_TYPE_NIUBIZ && (
    <>
      <NiubizFields selector={selector} form="ReceiverForm" />
      <FormGroup row>
        <FormItem>
          <Field
            name="niubizLink"
            validate={[isValidNiubizLink]}
            component={TextInput}
            type="hidden"
            disabled
          />
        </FormItem>
      </FormGroup>
    </>
  );

  const openpayForm = paymentMethod === TRANSACTION_TYPE_OPENPAY_LINK && (
    <>
      <OpenpayFields
        selector={selector}
        form="ReceiverForm"
        remainingPrice={remainingPrice}
      />
      <FormItem>
        <Field
          name="openpayLink"
          validate={[isValidNiubizLink]}
          component={TextInput}
          type="hidden"
          disabled
        />
      </FormItem>
    </>
  );

  const deliveryAddress = showDeliveryAddress ? (
    <>
      <h2>Delivery</h2>
      <FormGroup row>
        <FormItem label="Dirección">
          <Field
            name="deliveryAddress"
            component={CreatableSelect}
            options={deliveryAddresses}
            isLoading={isDeliveryAddressLoading}
            placeholder="Calle de ejemplo 123 - Distrito ejemplo"
            isValidNewOption={isValidNewDeliveryAddress}
            noOptionsMessage={handleNoDeliveryAddress}
            onCreateOption={handleCreateDeliveryAddress}
            validate={[isRequired]}
          />
        </FormItem>
      </FormGroup>
    </>
  ) : null;

  let freeOfChargeFields = null;
  if (showFreeOfChargeFields) {
    freeOfChargeFields = (
      <>
        <FormGroup row>
          <FormItem label="Categoría de Servicio a titulo gratuito" required>
            <Field
              name="freeOfChargeCategoryId"
              component={FreeOfChargeCategorySelect}
              validate={[isRequired]}
              onChange={(category) => {
                change('freeOfChargeSubCategory', null);
                change('freeOfChargeUser', null);

                dispatchGetFreeOfChargeCategory({
                  freeOfChargeCategoryId: category.value,
                });
              }}
            />
          </FormItem>
        </FormGroup>

        {showFreeOfChargeSubCategory && (
          <>
            <FormGroup row>
              <FormItem label="SubCategoría" required>
                <Field
                  name="freeOfChargeSubCategory"
                  component={Select}
                  options={freeOfChargeSubCategories}
                  validate={[isRequired]}
                  onChange={() => change('freeOfChargeUser', null)}
                />
              </FormItem>
            </FormGroup>

            <FormGroup row>
              <FormItem label="Autorizador" required>
                <Field
                  name="freeOfChargeUser"
                  component={Select}
                  options={freeOfChargeUsers}
                  validate={[isRequired]}
                />
              </FormItem>
            </FormGroup>
          </>
        )}
      </>
    );
  }

  return (
    <Form onSubmit={handleSubmit(handleReceiverSubmit)}>
      <CustomerInputGroup
        label="Destinatario"
        labelRequired
        name="customer"
        form="ReceiverForm"
        mobilePhoneRequired
        onChange={handleCustomerChange}
        showDetails
        showCreditNoteMessage
        onlyCustomer={passengers.length > 1 ? undefined : passengers[0].id}
        autoFocus
      />
      <FormGroup row>
        <FormItem label="Correo">
          <Field
            invalid
            name="email"
            component={CreatableSelect}
            options={emails}
            isLoading={isEmailLoading}
            formatCreateLabel={formatCreateLabel}
            validate={[isRequired, isValidEmail]}
            onCreateOption={handleCreateEmail}
            isValidNewOption={isValidNewEmail}
            noOptionsMessage={handleNoEmails}
            onChange={handleChangeEmail}
          />
        </FormItem>
      </FormGroup>
      {showDiscountSubCategory && (
        <>
          <FormGroup row>
            <FormItem label="Descuento SubCategoría" required>
              <Field
                name="discountCodeSubCategory"
                component={Select}
                options={discountCodeSubCategories}
                validate={[isRequired]}
                onChange={() =>
                  change('discountCodeSubCategoryAuthorizer', null)
                }
              />
            </FormItem>
          </FormGroup>
          <FormGroup row>
            <FormItem label="Autorizador" required>
              <Field
                name="discountCodeSubCategoryAuthorizer"
                component={Select}
                options={subCategoryAuthorizers}
                validate={[isRequired]}
              />
            </FormItem>
          </FormGroup>
        </>
      )}
      <VoucherTypeInputGroup
        voucherTypeFieldName="voucherType"
        businessFieldName="business"
        businessTaxIdFieldName="businessTaxId"
        businessNameFieldName="businessName"
        addressSummaryFieldName="addressSummary"
        change={change}
      />
      <FormGroup row>
        <FormItem label="Método de Pago">
          <Field
            name="paymentMethod"
            component={PaymentMethodSelect}
            onChange={handlePaymentMethodChange}
            validate={[isRequired]}
            isLoading={isPaymentMethodLoading}
            transactionCodesNotIncluded={[]}
          />
          {showCreditNoteSelect && passengers.length > 1 && (
            <p className="text-danger">
              Este método de pago sólo aplica para venta un solo pasaje
            </p>
          )}
        </FormItem>
      </FormGroup>
      {freeOfChargeFields}
      {passengers.length === 1 &&
        selectedCustomer &&
        selectedCustomer.value &&
        showCreditNoteSelect && (
          <FormGroup row>
            <FormItem label="Nota de crédito">
              <Field
                name="creditNotes"
                component={CreditNoteSelect}
                isMulti
                validate={[isRequired]}
                isCustomerIdRequired
                optionsRegistered
              />
            </FormItem>
          </FormGroup>
        )}

      {creditNotes.length > 0 && remainingPrice > 0 && (
        <>
          <h3>
            <b>{`Restante: ${numberFormatter({
              style: 'currency',
              value: remainingPrice,
            })}`}</b>
          </h3>
          <FormGroup row>
            <FormItem label="Siguiente método de Pago">
              <Field
                name="nextPaymentMethod"
                component={PaymentMethodSelect}
                onChange={handleNextPaymentMethodChange}
                validate={[isRequired]}
                isLoading={isPaymentMethodLoading}
                transactionCodesNotIncluded={[CREDIT_NOTE]}
              />
            </FormItem>
          </FormGroup>
        </>
      )}
      {voucherCode}
      {niubizForm}
      {openpayForm}
      {deliveryAddress}
      <div className="FormButtonGroup">
        <Button type="submit" disabled={submitting} color="primary" size="lg">
          Reservar <i className="fa fa-arrow-circle-right" />
        </Button>
      </div>
    </Form>
  );
};

ReceiverForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  emails: Select.propTypes.options.isRequired,
  isEmailLoading: PropTypes.bool.isRequired,
  deliveryAddresses: Select.propTypes.options.isRequired,
  isDeliveryAddressLoading: PropTypes.bool.isRequired,
  dispatchGetCustomerEmails: PropTypes.func.isRequired,
  dispatchClearCustomerEmails: PropTypes.func.isRequired,
  dispatchGetCustomerDeliveryAddresses: PropTypes.func.isRequired,
  dispatchClearCustomerDeliveryAddresses: PropTypes.func.isRequired,
  selectedCustomer: PropTypes.shape({
    value: PropTypes.number.isRequired,
    label: PropTypes.string.isRequired,
  }),
  isPaymentMethodLoading: PropTypes.bool.isRequired,
  dispatchGetPaymentMethods: PropTypes.func.isRequired,
  dispatchClearCreditNotes: PropTypes.func.isRequired,
  customerOrderId: PropTypes.number.isRequired,
  change: PropTypes.func.isRequired,
  submitting: PropTypes.bool.isRequired,
  dispatchGetVoucherTypes: PropTypes.func.isRequired,
  dispatchPostPostpaidBookingPretransactionV2: PropTypes.func.isRequired,
  passengers: passengersPropType.isRequired,
  creditNotes: PropTypes.instanceOf(Array),
  totalPrice: PropTypes.number.isRequired,
  discountCodeId: PropTypes.number,
  discountCodeSubCategories: PropTypes.instanceOf(Array),
  dispatchGetDiscountCodeSubCategoriesByDiscountId: PropTypes.func.isRequired,
  dispatchClearDiscountCodeSubCategories: PropTypes.func.isRequired,
  subCategoryAuthorizers: PropTypes.instanceOf(Array),
  freeOfChargeSubCategories: PropTypes.instanceOf(Array),
  freeOfChargeUsers: PropTypes.instanceOf(Array),
  dispatchGetFreeOfChargeCategory: PropTypes.func.isRequired,
  dispatchClearFreeOfChargeCategory: PropTypes.func.isRequired,
};

ReceiverForm.defaultProps = {
  selectedCustomer: null,
  creditNotes: [],
  discountCodeId: null,
  discountCodeSubCategories: [],
  subCategoryAuthorizers: [],
  freeOfChargeSubCategories: [],
  freeOfChargeUsers: [],
};

const mapStateToProps = (state) => {
  const emails = state.UserUnit.Customer.getIn([
    'emails',
    'content',
    'content',
  ]).map((customerEmail) => ({
    value: customerEmail.id,
    label: customerEmail.email,
  }));
  const isEmailLoading = state.UserUnit.Customer.getIn(['emails', 'loading']);

  const deliveryAddresses = state.UserUnit.Customer.getIn([
    'deliveryAddresses',
    'content',
    'content',
  ]).map((deliveryAddress) => ({
    value: deliveryAddress.id,
    label: deliveryAddress.deliveryAddress,
  }));
  const isDeliveryAddressLoading = state.UserUnit.Customer.getIn([
    'deliveryAddresses',
    'loading',
  ]);

  const selectedCustomer = selector(state, 'customer');

  const isPaymentMethodLoading = state.AccountingUnit.PaymentMethod.getIn([
    'all',
    'loading',
  ]);

  const voucherTypes = state.AccountingUnit.VoucherType.getIn([
    'all',
    'content',
    'content',
  ]).reduce((options, voucherType) => {
    if (voucherType.displayInPaymentsPage) {
      options.push({
        value: voucherType.id,
        label: voucherType.name,
      });
    }
    return options;
  }, []);
  const isVoucherTypeLoading = state.AccountingUnit.VoucherType.getIn([
    'all',
    'loading',
  ]);

  const customerOrderId = state.PostpaidBookingUnit.PostpaidBooking.getIn([
    'receiver',
    'customerOrder',
    'id',
  ]);

  const businesses = state.UserUnit.Business.getIn([
    'all',
    'content',
    'content',
  ]).map((business) => ({
    value: business.id,
    label: `${business.businessTaxId} - ${business.name}`,
  }));
  const isBusinessLoading = state.UserUnit.Business.getIn(['all', 'loading']);

  const creditNotes = selector(state, 'creditNotes');

  const discountCodeId = state.PostpaidBookingUnit.PostpaidBooking.getIn([
    'receiver',
    'customerOrder',
    'discountCodeId',
  ]);

  const rawSubcats = state.SalesUnit.DiscountCodeSubCategory.getIn([
    'all',
    'content',
    'content',
  ]);

  const discountCodeSubCategories = rawSubcats.map((subCategory) => ({
    value: subCategory.id,
    label: subCategory.name,
  }));

  const selectedSubcatField = selector(state, 'discountCodeSubCategory');
  const chosenSubcatId =
    (selectedSubcatField && selectedSubcatField.value) ||
    (discountCodeSubCategories.length === 1
      ? discountCodeSubCategories[0].value
      : null);

  let subCategoryAuthorizers = [];
  if (chosenSubcatId) {
    const sc = rawSubcats.find((s) => s.id === chosenSubcatId);
    if (sc && Array.isArray(sc.discountCodeSubCategoryAuthorizerList)) {
      subCategoryAuthorizers = sc.discountCodeSubCategoryAuthorizerList
        .filter((a) => a.active !== false)
        .map((a) => {
          const auth = a.discountCodeAuthorizer;
          const label =
            auth && auth.customer && auth.customer.fullName
              ? auth.customer.fullName
              : `Autorizador ${a.discountCodeAuthorizerId}`;
          return {
            value: a.discountCodeAuthorizerId,
            label,
          };
        });
    }
  }

  const categoryState = state.SalesUnit.FreeOfChargeCategory.getIn([
    'current',
    'content',
  ]);

  let freeOfChargeSubCategories = [];
  let freeOfChargeUsers = [];

  const category =
    categoryState && categoryState.toJS ? categoryState.toJS() : categoryState;

  if (category) {
    const subcats = category.freeOfChargeSubCategoryList || [];

    freeOfChargeSubCategories = subcats.map((sc) => ({
      value: sc.id,
      label: sc.name,
    }));

    const selectedSubcat = selector(state, 'freeOfChargeSubCategory');

    if (selectedSubcat) {
      const found = subcats.find((sc) => sc.id === selectedSubcat.value);

      if (found) {
        freeOfChargeUsers = (found.freeOfChargeSubCategoryUserList || []).map(
          (u) => ({
            value: u.customer.id,
            label: u.customer.fullName,
          }),
        );
      }
    }
  }

  return {
    emails,
    isEmailLoading,
    deliveryAddresses,
    isDeliveryAddressLoading,
    selectedCustomer,
    isPaymentMethodLoading,
    voucherTypes,
    isVoucherTypeLoading,
    customerOrderId,
    businesses,
    isBusinessLoading,
    creditNotes,
    discountCodeId,
    discountCodeSubCategories,
    subCategoryAuthorizers,
    freeOfChargeSubCategories,
    freeOfChargeUsers,
  };
};

const mapDispatchToProps = {
  dispatchGetCustomerEmails: getCustomerEmails,
  dispatchClearCustomerEmails: clearCustomerEmails,
  dispatchGetCustomerDeliveryAddresses: getCustomerDeliveryAddresses,
  dispatchClearCustomerDeliveryAddresses: clearCustomerDeliveryAddresses,
  dispatchGetPaymentMethods: getPaymentMethods,
  dispatchGetVoucherTypes: getVoucherTypes,
  dispatchClearCreditNotes: clearCreditNotes,
  dispatchPostPostpaidBookingPretransactionV2:
    postPostpaidBookingPretransactionV2,
  dispatchGetDiscountCodeSubCategoriesByDiscountId:
    getDiscountCodeSubCategoriesByDiscountId,
  dispatchClearDiscountCodeSubCategories: clearDiscountCodeSubCategories,
  dispatchGetFreeOfChargeCategory: getFreeOfChargeCategory,
  dispatchClearFreeOfChargeCategory: clearFreeOfChargeCategory,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(
  reduxForm({
    form: 'ReceiverForm',
  })(ReceiverForm),
);
